// The feature-owned "close every open connector surface" seam — the sibling of
// `providerConnectedBus`, and the thing a scope edge drives (ORP-20384).
//
// WHY A REGISTRY AND NOT A WATCHER INSIDE EACH MODAL. `useConnector` is instantiated once per tile
// inside `ConnectorTile`'s own `setup()` — that is the entire reason the tile component exists — so
// its `isOpen` is local state no lifecycle registrant can reach. Giving every instance its own
// `watch(workspaceId)` would hang a watcher off each of the 37 tiles, and would still miss the
// session edge and `useConnections`' reauth modal, which is not a `useConnector` at all. Publishing
// an idempotent `close` instead keeps ONE watcher, in `registerIntegrationsLifecycle`.
//
// This is NOT v3's instance map (`IntegrationsLoop.vue:46-57`): nothing is keyed, nothing is read
// back, and nothing is reached into. The only thing published is a nullary close.
import { getCurrentScope, onScopeDispose } from 'vue'

export type CloseConnectorSurface = () => void

const surfaces = new Set<CloseConnectorSurface>()

/**
 * Publish one surface's `close`, so a workspace switch or a logout can shut it.
 *
 * Returns its own disposer AND — when called from inside an effect scope, which every production
 * caller is — detaches on scope teardown. Composable specs call these composables bare, where
 * `onScopeDispose` would only warn and drop the callback on the floor; the guard keeps a stale
 * `close` from outliving its owner in either shape.
 */
export function registerConnectorSurface(close: CloseConnectorSurface): () => void {
  surfaces.add(close)
  const unregister = (): void => {
    surfaces.delete(close)
  }
  if (getCurrentScope()) onScopeDispose(unregister)
  return unregister
}

/** Close every registered surface. Closing is idempotent, so an already-closed one is a no-op. */
export function closeConnectorSurfaces(): void {
  // Iterated over a COPY: closing a modal unmounts it, and the unmount unregisters mid-iteration.
  for (const close of [...surfaces]) close()
}
